<script setup lang="ts">
import ReplacementDetectionItem from './ReplacementDetectionItem.vue';
import type {SelectedReplacement} from '../../types/replacement_detection.ts';
import {SvgIcon} from '../../../svg.ts';

defineProps<{
  replacements: SelectedReplacement[];
}>();

const emit = defineEmits<{
  confirm: [];
  skip: [];
  selectAll: [];
  unselectAll: [];
}>();
</script>

<template>
  <div>
    <h3 class="tw:mb-2">
      Potential File Replacements Detected
    </h3>
    <p class="tw:text-text-light-2 tw:mb-4">
      The following uploaded files appear to be renamed versions of existing files.
      Any checked items below will be replaced with the newly uploaded files.
    </p>

    <div class="results-container-header">
      <div class="select-actions-container">
        <a
          href="#"
          @click.prevent="emit('selectAll')"
        >Select all</a>
        <a
          href="#"
          @click.prevent="emit('unselectAll')"
        >Unselect all</a>
      </div>
      <div class="similarity-header">
        Confidence
        <span
          class="similarity-header-info"
          data-tooltip-content="AllSpice tries to match updated files based on a combination of path, file name, and content."
          data-position="top-center"
          data-variation="inverted tiny"
        >
          <SvgIcon
            name="octicon-info"
            :size="14"
          />
        </span>
      </div>
    </div>

    <div class="replacement-results-container">
      <ReplacementDetectionItem
        v-for="item in replacements"
        :key="item.candidate.source + '-' + item.candidate.dest"
        v-model:checked="item.checked"
        :source="item.candidate.source"
        :dest="item.candidate.dest"
        :similarity="item.candidate.similarity"
      />
    </div>

    <div class="form-actions-container">
      <button
        class="ui primary button"
        @click="emit('confirm')"
      >
        Confirm Replacements
      </button>
      <button
        class="ui button"
        @click="emit('skip')"
      >
        Skip (treat all files as new)
      </button>
    </div>
  </div>
</template>

<style scoped>
@reference "#index.css";

.results-container-header {
  @apply tw:flex tw:justify-between tw:mb-2;
}

.select-actions-container {
  @apply tw:flex tw:gap-3;
}

.similarity-header {
  @apply tw:font-semibold tw:text-sm tw:text-text-light-2 tw:flex-shrink-0 tw:px-4 tw:flex tw:items-center tw:gap-1;
}

.similarity-header-info {
  @apply tw:flex tw:items-center tw:cursor-help;
}

.replacement-results-container {
  @apply tw:border tw:border-solid tw:border-secondary-alpha-40 tw:rounded tw:px-4 tw:py-2 tw:mb-4;
}

.form-actions-container {
  @apply tw:flex tw:gap-2;
}
</style>
